import { sessionSignal } from "@/api/session";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  CandlestickSeries,
  CrosshairMode,
  LineSeries,
  LineStyle,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";
import type { Backtest, OrderRecord } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { usePageAlive } from "@/hooks/usePageAlive";
import { useTheme } from "@/hooks/useTheme";
import { useFormatDate } from "@/hooks/useFormatDate";
import { Card } from "@/components/ui/kit";
import { Dialog } from "@/components/ui/Dialog";
import { PREVIEW_TIMEOUT_MS, buildPreviewStreamPayload } from "@/lib/backtest-run";
import { barsAgoDate } from "@/lib/chart-tail";
import { timeframeMinutes } from "@/lib/new-backtest";
import { matchRoundTrips } from "@/lib/order-trades";
import { StreamAccumulator, resolvePreviewStreamUrl, streamEvents } from "@/lib/preview-stream";

type Candle = { time: number; open: number; high: number; low: number; close: number };

/** Candles on screen by default, and the most a strategy's older trades can stretch it to. */
const MIN_BARS = 300;
const MAX_BARS = 1500;
const UP = "#26a69a";
const DOWN = "#ef5350";

const num = (v: number, digits = 4) => v.toLocaleString("en-US", { maximumFractionDigits: digits });
const money2 = (v: number) =>
  `${v > 0 ? "+" : v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qtyOf = (o: OrderRecord) => Number(o.executed_qty) || Number(o.requested_qty) || 0;

/**
 * Live strategy: the position it holds now, and on request (the page's Show
 * chart, `chartOpen`) a plain TradingView chart, in a dialog, of where its real
 * orders filled. The candles are fetched
 * once (a short stream, cut as soon as they arrive) and stay as they are until
 * Refresh; nothing runs per candle. The position needs them too (price now).
 */
export function LiveTradesChart({
  backtest,
  accountId,
  chartOpen,
  onCloseChart,
}: {
  backtest: Backtest;
  accountId: string;
  chartOpen: boolean;
  onCloseChart: () => void;
}) {
  const api = useApi(["scripts"]);
  const { theme } = useTheme();
  const { dateTimeShort } = useFormatDate();
  const hostRef = useRef<HTMLDivElement>(null);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [candles, setCandles] = useState<Candle[] | null>(null);
  const [asOf, setAsOf] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const loadSeq = useRef(0);
  /** The candle stream being read, so leaving the page cuts it at once. */
  const streamRef = useRef<AbortController | null>(null);
  const pageAlive = usePageAlive();
  const requestRef = useRef(api.commands);
  requestRef.current = api.commands;
  const btRef = useRef(backtest);
  btRef.current = backtest;

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const bt = btRef.current;
    const request = requestRef.current;
    setLoading(true);
    setFailed(false);
    streamRef.current?.abort();
    const controller = new AbortController();
    streamRef.current = controller;
    const signal = AbortSignal.any([controller.signal, sessionSignal()]);
    try {
      // The strategy's own orders first: they decide how far back the candles go.
      const rows = (await request.listOrders({ account_id: accountId, backtest_id: bt.id, limit: 200 })) as OrderRecord[] | null;
      if (seq !== loadSeq.current) return;
      const own = (Array.isArray(rows) ? rows : []).filter((o) => o.backtest_id === bt.id);
      setOrders(own);

      const tfMs = (timeframeMinutes(bt.timeframe || "") || 0) * 60_000;
      const filled = own.filter((o) => o.status === "done").slice(0, 8);
      const oldest = filled.length ? Date.parse(filled[filled.length - 1]!.created_at) : NaN;
      const wanted = tfMs && Number.isFinite(oldest) ? Math.ceil((Date.now() - oldest) / tfMs) + 40 : 0;
      const start = barsAgoDate(bt.timeframe, Math.min(MAX_BARS, Math.max(MIN_BARS, wanted)));
      if (!start) throw new Error("unknown timeframe");

      const link = (await request.createPreviewStream(buildPreviewStreamPayload({ ...bt, start_date: start, scripts: api.scripts || [] }),
        PREVIEW_TIMEOUT_MS, signal
      )) as { url: string };
      if (seq !== loadSeq.current) return;
      const acc = new StreamAccumulator();
      acc.candlesAndPlotsOnly = true;
      for await (const ev of streamEvents(resolvePreviewStreamUrl(link.url), signal)) {
        if (seq !== loadSeq.current) break;
        acc.ingest(ev);
        if (ev.t === "historicalDone" || ev.t === "done") break;
      }
      if (seq !== loadSeq.current) return;
      const out: Candle[] = [];
      for (let i = 0; i < acc.nBars; i++) {
        out.push({ time: Math.floor(acc.bars.time[i]! / 1000), open: acc.bars.open[i]!, high: acc.bars.high[i]!, low: acc.bars.low[i]!, close: acc.bars.close[i]! });
      }
      setCandles(out);
      setAsOf(Date.now());
    } catch {
      if (seq === loadSeq.current) setFailed(true);
    } finally {
      // The candles are in: stop the stream instead of following live ones.
      controller.abort();
      if (streamRef.current === controller) streamRef.current = null;
      if (seq === loadSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    if (!api.ready || !accountId) return;
    void load();
    return () => {
      // A counter, not a DOM ref: bumping the latest value is the point (drops in-flight loads).
      // eslint-disable-next-line react-hooks/exhaustive-deps
      loadSeq.current++;
      streamRef.current?.abort();
      streamRef.current = null;
    };
  }, [api.ready, accountId, backtest.id, load]);

  const trips = useMemo(() => matchRoundTrips(orders), [orders]);
  const open = trips.find((t) => t.open) ?? null;
  const last = candles?.length ? candles[candles.length - 1]! : null;

  useEffect(() => {
    const el = hostRef.current;
    // Removed on unmount and when the page is left, so its canvas and data are freed.
    if (!el || !chartOpen || !candles?.length || !pageAlive) return;
    const dark = theme === "dark";
    const grid = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
    const border = dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";
    const chart: IChartApi = createChart(el, {
      autoSize: true,
      layout: { background: { color: "transparent" }, textColor: dark ? "#A1A1AA" : "#52525B", attributionLogo: true },
      grid: { vertLines: { color: grid }, horzLines: { color: grid } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: border },
      timeScale: { borderColor: border, timeVisible: true, secondsVisible: false },
    });
    const series = chart.addSeries(CandlestickSeries, { upColor: UP, downColor: DOWN, borderVisible: false, wickUpColor: UP, wickDownColor: DOWN });
    series.setData(candles.map((c) => ({ ...c, time: c.time as Time })));

    // An order belongs to the candle that was open when it filled.
    const first = candles[0]!.time;
    const candleOf = (iso: string | null): number | null => {
      const t = Math.floor(Date.parse(iso || "") / 1000);
      if (!Number.isFinite(t) || t < first) return null;
      let lo = 0;
      let hi = candles.length - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (candles[mid]!.time <= t) lo = mid;
        else hi = mid - 1;
      }
      return candles[lo]!.time;
    };

    // Result of each closing order, to print next to its marker.
    const pnlAt = new Map<string, number>();
    for (const t of trips) if (!t.open && t.pnl != null && t.exitTime) pnlAt.set(t.exitTime, (pnlAt.get(t.exitTime) || 0) + t.pnl);
    const markers: SeriesMarker<Time>[] = [];
    for (const o of [...orders].reverse()) {
      if (o.status !== "done") continue;
      const time = candleOf(o.created_at);
      if (time == null) continue;
      const buy = o.side === "BUY";
      const pnl = pnlAt.get(o.created_at);
      markers.push({
        time: time as Time,
        position: buy ? "belowBar" : "aboveBar",
        color: buy ? UP : DOWN,
        shape: buy ? "arrowUp" : "arrowDown",
        text: `${buy ? "Buy" : "Sell"} ${num(qtyOf(o), 6)}${pnl != null ? ` · ${money2(pnl)}` : ""}`,
      });
    }
    createSeriesMarkers(series, markers);

    // Each closed trade as a line from its entry to its exit.
    for (const t of trips.slice(0, 40)) {
      if (t.open || t.entryPrice == null || t.exitPrice == null) continue;
      const a = candleOf(t.entryTime);
      const b = candleOf(t.exitTime);
      if (a == null || b == null || b <= a) continue;
      chart
        .addSeries(LineSeries, {
          color: (t.pnl ?? 0) >= 0 ? UP : DOWN,
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        })
        .setData([
          { time: a as Time, value: t.entryPrice },
          { time: b as Time, value: t.exitPrice },
        ]);
    }

    if (open?.entryPrice != null) {
      series.createPriceLine({
        price: open.entryPrice,
        color: dark ? "#E4E4E7" : "#18181B",
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: `${open.side === "long" ? "Long" : "Short"} ${num(open.qty, 6)} entry`,
      });
    }
    chart.timeScale().setVisibleLogicalRange({ from: Math.max(0, candles.length - 160), to: candles.length + 6 });
    return () => chart.remove();
  }, [candles, orders, trips, open, theme, pageAlive, chartOpen]);

  if (!accountId) return null;

  const unrealized = open?.entryPrice && last ? (last.close - open.entryPrice) * open.qty * (open.side === "long" ? 1 : -1) : null;

  const refresh = (
    <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={() => void load()}>
      <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
      Refresh
    </button>
  );

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-4">
        <h2 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">Position</h2>
        {refresh}
      </div>

      {/* Just what it holds and how it stands; where it got in is on the chart. */}
      <div className="px-4 pb-4 pt-2">
        {open ? (
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="rounded-full px-2 py-0.5 text-xs font-semibold"
                  style={
                    open.side === "long"
                      ? { background: "var(--success-soft)", color: "var(--success)" }
                      : { background: "var(--danger-soft)", color: "var(--danger)" }
                  }
                >
                  {open.side === "long" ? "Long" : "Short"}
                </span>
                <b className="num text-[17px] font-semibold text-[var(--text-primary)]">
                  {num(open.qty, 6)} {open.symbol}
                </b>
              </div>
              <div className="mt-1 text-xs text-[var(--text-muted)]">since {dateTimeShort(open.entryTime)}</div>
            </div>
            <div className="text-right">
              <div
                className="num text-[22px] font-semibold"
                style={{ color: unrealized == null ? "var(--text-muted)" : unrealized >= 0 ? "var(--success)" : "var(--danger)" }}
                title="From the entry to the last candle, without fees"
              >
                {unrealized == null ? "—" : money2(unrealized)}
              </div>
              <div className="text-xs text-[var(--text-muted)]">unrealized</div>
            </div>
          </div>
        ) : (
          <div className="text-[13px] text-[var(--text-muted)]">{loading ? "Loading…" : "No open position."}</div>
        )}
      </div>

      {chartOpen && (
        <Dialog
          className="max-w-[1180px]"
          title={
            <span className="flex flex-wrap items-baseline gap-2.5">
              Trades on the chart
              <span className="text-xs font-normal text-[var(--text-muted)]">Where this strategy's real orders filled</span>
            </span>
          }
          onClose={onCloseChart}
        >
          <div className="mb-2 flex items-center justify-end gap-2.5">
            {asOf && !loading && <span className="text-xs text-[var(--text-muted)]">As of {dateTimeShort(new Date(asOf))}</span>}
            {refresh}
          </div>
          <div className="relative h-[min(70vh,620px)] overflow-hidden rounded-[10px] border border-[var(--border)]">
            <div ref={hostRef} className="h-full w-full" />
            {!candles?.length && (
              <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-[13px] text-[var(--text-muted)]">
                {loading ? "Loading candles…" : failed ? "The chart couldn't load. Try Refresh." : "No candles for this market yet."}
              </div>
            )}
          </div>
        </Dialog>
      )}

    </Card>
  );
}
